<!-- The sidebar's FAQ (Seán, 2026-10-22, issues F1–F4; reorganised the same day): three groups,
     each a heading (always shown) and its questions; a question is a line that opens its answer
     in place, at most one open (as /admin/roadmap's items; Seán, 2026-10-22). The open one is in
     the address (#faq&<id>), so a link opens it. -->
<template>
  <section class="faq" aria-label="Frequently asked questions" @keydown.esc="closeOnEscape">
    <section v-for="group in items" :key="group.heading" class="group">
      <h4>{{ group.heading }}</h4>
      <ul>
        <li
          v-for="item in group.items"
          :key="item.id"
          :class="{isOpen: isOpen(item.id)}"
          @click="focusQuestion">
          <button
            type="button"
            class="question"
            :aria-expanded="isOpen(item.id)"
            :aria-controls="`faq-answer-${item.id}`"
            @click="toggle(item.id)">
            <span><SidebarText :parts="[item.question].flat()" /></span>
            <svg class="caret" viewBox="0 0 12 12" aria-hidden="true">
              <path d="M4 2l4 4-4 4" />
            </svg>
          </button>
          <!-- opening and closing by height (Seán, 2026-10-22); at once with reduced motion -->
          <Transition
            @before-enter="onBeforeEnter"
            @enter="onEnter"
            @after-enter="onAfterEnter"
            @before-leave="onBeforeLeave"
            @leave="onLeave"
            @after-leave="clearHeight">
            <div v-if="isOpen(item.id)" :id="`faq-answer-${item.id}`" class="answerBox">
              <p class="answer"><SidebarText :parts="item.answer" /></p>
            </div>
          </Transition>
        </li>
      </ul>
    </section>
  </section>
</template>

<script setup>
import SidebarText from "@/components/YearSidebar/SidebarText.vue";
const props = defineProps({
  items: {type: Array, required: true}, // sidebar.json FAQ: [{heading, items}]
  answer: {type: String, default: ""} // the open one's id (the address's)
});
const emit = defineEmits(["open"]);
// an answer's height animates from 0 to its own, and back (.answerBox's transition); with
// reduced motion, none, so no transitionend: done at once
const isStill = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;
function whenEnded(element, done) {
  if (isStill()) return done();
  element.addEventListener("transitionend", done, {once: true});
}
const onBeforeEnter = (element) => (element.style.height = "0");
function onEnter(element, done) {
  element.style.height = `${element.scrollHeight}px`;
  whenEnded(element, done);
}
const onBeforeLeave = (element) => (element.style.height = `${element.scrollHeight}px`);
function onLeave(element, done) {
  void element.offsetHeight; // the height above applied first, so the change animates
  element.style.height = "0";
  whenEnded(element, done);
}
const clearHeight = (element) => (element.style.height = "");
// an answer opened by a click, cut off below the window: the page scrolls just enough to show
// it, never taking its question above the sticky banner (Seán, 2026-10-22; NN/g: moving an
// opened item to the top reads as a new page). Not for one open on arrival (#faq&…)
let clickedId = "";
// a click anywhere in a question or its answer puts the focus on the question (Seán, 2026-10-22:
// Safari and Firefox on a Mac don't focus a clicked button), so the outline shows where it is;
// not a link's click (it goes where it goes)
function focusQuestion(event) {
  if (event.target.closest("a")) return;
  event.currentTarget.querySelector(".question")?.focus({preventScroll: true});
}
// at most one open (Seán, 2026-10-22): the address's; opening another closes it
const isOpen = (id) => id === props.answer;
// Escape, with keyboard focus in the FAQ: the open answer closes (Seán, 2026-10-22), and the focus
// goes to its question (a link in the answer would vanish with it); Escape goes no further
function closeOnEscape(event) {
  if (!props.answer) return;
  event.stopPropagation();
  const question = document.querySelector(`[aria-controls="faq-answer-${props.answer}"]`);
  emit("open", "");
  question?.focus();
}
function toggle(id) {
  const isOpening = !isOpen(id);
  clickedId = isOpening ? id : "";
  emit("open", isOpening ? id : "");
}
// the bottom of what stays put at the top: the sidebar's control when the top is pinned
// (usePinnedTop.js), else the banner (the body's top padding)
function getTopEdge() {
  const control = document.querySelector(".isPinnedTop .segmented");
  if (control) return control.getBoundingClientRect().bottom;
  return parseFloat(getComputedStyle(document.body).paddingTop) || 0;
}
const GAP = 12; // px: room left below the answer, and below the banner above the question
function getScrollBy(item) {
  const {top, bottom} = item.getBoundingClientRect();
  const banner = getTopEdge(); // what stays at the top: the banner, or all the pinned top
  const overflow = bottom + GAP - window.innerHeight;
  if (overflow <= 0) return 0;
  return Math.min(overflow, top - banner - GAP);
}
// once open, and after a frame, so the one closing has gone too (both take the same time)
function onAfterEnter(element) {
  clearHeight(element);
  if (element.id !== `faq-answer-${clickedId}`) return;
  clickedId = "";
  requestAnimationFrame(() => {
    const distance = getScrollBy(element.closest("li"));
    if (distance > 0) window.scrollBy({top: distance, behavior: isStill() ? "auto" : "smooth"});
  });
}
</script>

<style scoped>
.answerBox {
  overflow: hidden;
  transition: height 0.25s ease;
}
@media (prefers-reduced-motion: reduce) {
  .answerBox {
    transition: none;
  }
}
/* a group's heading (Seán, 2026-10-22): small, in capitals, bold and muted, so the questions
   lead; never opens. The first sits in the band below the control (YearSidebar.vue); the others
   have room above and a faint line between the groups */
h4 {
  margin: 0 0 0.2rem;
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--text-color-less-subtle);
}
.group + .group {
  margin-top: 1rem;
  padding-top: 0.9rem;
  border-top: 1px solid var(--border-color-subtle);
}
ul {
  margin: 0;
  padding: 0;
  list-style: none;
}
/* no bullets (global.css gives every list item one), no line between questions: space instead */
li {
  margin: 0;
  padding: 0;
  list-style: none;
}
li::marker {
  content: none;
}
/* a question: the whole line opens it, its text from the left, the caret on the right beside its
   first line; not a button's look (global.css) */
.question {
  position: relative;
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.6em;
  width: 100%;
  padding: 0.35em 0;
  font: inherit;
  font-size: 0.95em;
  text-align: left;
  color: inherit;
  background: none;
  border: none;
  border-radius: 0;
  box-shadow: none;
  cursor: pointer;
}
/* hover: subtle (Seán, 2026-10-22: the green was too strong): the question brightens a little
   (light text on the dark theme; black, on the light theme, stays as it is) and its caret comes
   up to full strength. No theme selector: :global(.darkMode) here leaks to the whole page */
.question:hover span {
  filter: brightness(1.25);
}
.question:hover .caret {
  opacity: 1;
}
/* the open question's green: the brand's, darker on the light theme (global.css
   --brand-text-dark-share), for the text, the caret and the outline */
.question {
  --open-color: color-mix(
    in srgb,
    var(--brand-dark-color) var(--brand-text-dark-share),
    var(--brand-color)
  );
}
.isOpen .question {
  font-weight: 600;
  color: var(--open-color);
}
/* the open question's outline (Seán, 2026-10-22): drawn outside the row, into the gutter on the
   left (the column's right edge clips: #main_content, overflow hidden), so the text keeps its
   place and width. It shows the focus (Seán, 2026-10-22): on an open question, while the focus
   is in it or its answer (a click there puts it there: focusQuestion); gone once the focus
   leaves, the question staying green. Keyboard focus on a closed question: the site's dark
   green; on an open one, thicker */
.question::before {
  content: "";
  position: absolute;
  inset: -1px 0 -1px -6px;
  border: 0 solid var(--open-color);
  border-radius: 6px;
  pointer-events: none;
}
.isOpen:focus-within .question::before {
  border-width: 1.5px;
}
.question:is(:focus-visible, .showFocusOutline) {
  outline: none !important;
}
/* the browser's own keyboard focus only: the site's .showFocusOutline (useFocusHandlers.js) can
   stay on a question after a click, so a closed one kept its outline (Seán, 2026-10-22) */
.question:focus-visible::before {
  border-width: 2px;
  border-color: var(--brand-dark-color);
}
.isOpen .question:focus-visible::before {
  border-width: 2.5px;
  border-color: var(--open-color);
}
.caret {
  flex: none;
  width: 0.7em;
  height: 0.7em;
  fill: none;
  stroke: currentColor;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
  opacity: 0.7;
  transform: translateX(-4px); /* clear of the outline's right edge; the text's width unchanged */
  transition: transform 0.15s ease;
}
.isOpen .caret {
  opacity: 1;
  transform: translateX(-4px) rotate(90deg);
}
/* the answer: muted, about 8 px below its question's row */
.answer {
  margin: 0.55em 0 0.75em;
  color: var(--text-color-less-subtle);
}
</style>
